"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { Input } from "./input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./select";

export interface ManagePayoutFormData {
    country: string;
    address: string;
    account_type: "savings" | "current";
    beneficiary_name: string;
    bank_account: string;
    ifsc: string;
}

export interface ManagePayoutBottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdate: (data: ManagePayoutFormData) => Promise<void>;
    initialData?: Partial<ManagePayoutFormData>;
    isLoading?: boolean;
}

const SUPPORTED_COUNTRIES = [
    { code: "IN", name: "India", currency: "INR" },
    { code: "US", name: "United States", currency: "USD" },
    { code: "GB", name: "United Kingdom", currency: "GBP" },
    { code: "CA", name: "Canada", currency: "CAD" },
    { code: "AU", name: "Australia", currency: "AUD" },
    { code: "SG", name: "Singapore", currency: "SGD" },
    { code: "AE", name: "United Arab Emirates", currency: "AED" },
];

export function ManagePayoutBottomSheet({
    isOpen,
    onClose,
    onUpdate,
    initialData,
    isLoading = false,
}: ManagePayoutBottomSheetProps) {
    const [formData, setFormData] = useState<ManagePayoutFormData>({
        country: initialData?.country || "IN",
        address: initialData?.address || "",
        account_type: initialData?.account_type || "savings",
        beneficiary_name: initialData?.beneficiary_name || "",
        bank_account: initialData?.bank_account || "",
        ifsc: initialData?.ifsc || "",
    });

    const [countrySearch, setCountrySearch] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Partial<Record<keyof ManagePayoutFormData, string>>>({});

    // Update form data when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData({
                country: initialData.country || "IN",
                address: initialData.address || "",
                account_type: initialData.account_type || "savings",
                beneficiary_name: initialData.beneficiary_name || "",
                bank_account: initialData.bank_account || "",
                ifsc: initialData.ifsc || "",
            });
        }
    }, [initialData]);

    const filteredCountries = SUPPORTED_COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const selectedCountry = SUPPORTED_COUNTRIES.find(
        (c) => c.code === formData.country
    );

    const handleInputChange = (
        field: keyof ManagePayoutFormData,
        value: string
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error when user types
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const validateForm = () => {
        const newErrors: Partial<Record<keyof ManagePayoutFormData, string>> = {};

        if (!formData.country) newErrors.country = "Country is required";
        if (!formData.address?.trim()) newErrors.address = "Address is required";
        if (!formData.account_type) newErrors.account_type = "Account type is required";
        if (!formData.beneficiary_name?.trim()) newErrors.beneficiary_name = "Account holder name is required";

        // Basic validation for bank account (digits only)
        if (!formData.bank_account?.trim()) {
            newErrors.bank_account = "Account number is required";
        } else if (!/^\d+$/.test(formData.bank_account.trim())) {
            newErrors.bank_account = "Account number must contain digits only";
        }

        // Basic IFSC validation (alphanumeric only)
        if (!formData.ifsc?.trim()) {
            newErrors.ifsc = "IFSC code is required";
        } else if (!/^[A-Z0-9]+$/.test(formData.ifsc.trim())) {
            newErrors.ifsc = "IFSC code must be alphanumeric only";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            await onUpdate(formData);
            onClose();
        } catch (error) {
            console.error("Failed to update payout details:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            className="bg-white px-4 pb-6 pt-3 max-h-[85vh]"
            showDragHandle
        >
            <div className="space-y-6 text-[#344054]">
                {/* Header */}
                <div className="space-y-1">
                    <p className="font-plus-jakarta-sans text-base font-semibold">Manage Payouts</p>
                    <p className="text-sm text-[#475467]">
                        View and modify your payout settings.
                    </p>
                </div>

                <div className="space-y-5">
                    {/* Country Selector */}
                    <div className="space-y-2">
                        <label htmlFor="country-select" className="text-sm font-medium text-[#667085]">
                            Select Country <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Input
                                id="country-search"
                                startIcon={
                                    <svg
                                        className="w-4 h-4 text-[#667085]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                }
                                placeholder={selectedCountry?.name || "Search country..."}
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                onFocus={() => setCountrySearch("")}
                                className="h-[46px]"
                            />
                            {countrySearch && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-black rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredCountries.map((country) => (
                                        <button
                                            key={country.code}
                                            type="button"
                                            onClick={() => {
                                                handleInputChange("country", country.code);
                                                setCountrySearch("");
                                            }}
                                            className="w-full px-3 py-2 text-left text-sm text-[#344054] hover:bg-[#F9FAFB]"
                                        >
                                            {country.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {errors.country && <p className="text-xs text-red-500">{errors.country}</p>}
                        <p className="text-xs text-[#667085]">
                            Request support for your country{" "}
                            <a href="#" className="text-[#0D41E1] hover:underline" onClick={(e) => e.preventDefault()}>
                                here
                            </a>
                            .
                        </p>
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                        <label htmlFor="address" className="text-sm font-medium text-[#667085]">
                            Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="address"
                            rows={3}
                            placeholder="Enter your full address"
                            value={formData.address}
                            onChange={(e) => handleInputChange("address", e.target.value)}
                            className="flex w-full bg-white text-sm text-gray-900 placeholder:text-gray-400 border border-black rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none"
                        />
                        {errors.address && <p className="text-xs text-red-500">{errors.address}</p>}
                    </div>

                    {/* Account Type */}
                    <div className="space-y-2">
                        <label htmlFor="account-type" className="text-sm font-medium text-[#667085]">
                            Account Type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.account_type}
                            onValueChange={(value) => handleInputChange("account_type", value)}
                        >
                            <SelectTrigger id="account-type" className="h-[46px]">
                                <SelectValue placeholder="Select account type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="savings">Savings</SelectItem>
                                <SelectItem value="current">Current</SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.account_type && <p className="text-xs text-red-500">{errors.account_type}</p>}
                    </div>

                    {/* Account Holder Name */}
                    <div className="space-y-2">
                        <label htmlFor="beneficiary-name" className="text-sm font-medium text-[#667085]">
                            Account holder name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="beneficiary-name"
                            placeholder="Enter account holder name"
                            value={formData.beneficiary_name}
                            onChange={(e) => handleInputChange("beneficiary_name", e.target.value)}
                            className="h-[46px]"
                        />
                        {errors.beneficiary_name && <p className="text-xs text-red-500">{errors.beneficiary_name}</p>}
                    </div>

                    {/* Account Number - Password Style */}
                    <div className="space-y-2">
                        <label htmlFor="bank-account" className="text-sm font-medium text-[#667085]">
                            Account Number <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="bank-account"
                            type="password"
                            placeholder="Enter account number"
                            value={formData.bank_account}
                            onChange={(e) => handleInputChange("bank_account", e.target.value)}
                            className="h-[46px] font-[Inter,sans-serif]"
                            style={{ fontVariantNumeric: "slashed-zero" }}
                        />
                        {errors.bank_account && <p className="text-xs text-red-500">{errors.bank_account}</p>}
                    </div>

                    {/* IFSC Code - Password Style */}
                    <div className="space-y-2">
                        <label htmlFor="ifsc" className="text-sm font-medium text-[#667085]">
                            IFSC Code <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="ifsc"
                            type="password"
                            placeholder="Enter IFSC code"
                            value={formData.ifsc}
                            onChange={(e) => handleInputChange("ifsc", e.target.value.toUpperCase())}
                            className="h-[46px] font-[Inter,sans-serif]"
                            style={{ fontVariantNumeric: "slashed-zero" }}
                        />
                        {errors.ifsc && <p className="text-xs text-red-500">{errors.ifsc}</p>}
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-3 pt-2">
                    <Button
                        variant="outline"
                        className="flex-1 rounded-lg"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <Button
                        className="flex-1 rounded-lg"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoading}
                    >
                        {isSubmitting ? "Updating..." : "Update"}
                    </Button>
                </div>
            </div>
        </BottomSheet>
    );
}
